import React, {useCallback, useEffect, useMemo, useState} from 'react';
import IMG from "../constants/image";
import {text} from "../constants/text";
import {OrderTabs} from "../components/Order/OrderTabs";
import {SearchBar} from "../components/Order/SearchBar";
import {OrderGroup} from "../components/Order/OrderGroup";
import cn from "classnames";
import IconText from "../components/Common/Text/IconText";
import Button from "../components/Common/Button";
import {PRODUCT_STATUS} from "../constants";
import {fetchOrderCountStatusAllTime, fetchOrders} from "../services/orderServices";
import {useOrdersStore} from "../store/orderStore";
import appStore from "../store/appStore";
import {useNavigate, useSearchParams} from "react-router-dom";
import Empty from "../components/Empty/Empty";
import {isMobile} from "react-device-detect";
import Loading from "../components/Common/Loading/Loading";
import Pagination from "../components/Pagination/Pagination";
import {isEmpty} from "lodash";
import {fetchAddresses} from "../services/addressServices";
import {IconArrowDown, IconArrowUp} from "@tabler/icons-react";
import {clsx} from "clsx";

const ORDER = {
  ASC: 'ASC',
  DESC: 'DESC',
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchStatus = searchParams.get('status');
  const searchQuery = searchParams.get('query') || '';
  const currentPageFromUrl = parseInt(searchParams.get('page') || '1');
  const sortField = searchParams.get('sort_field') || 'created_at';
  const sortDirection = searchParams.get('sort_direction') || ORDER.DESC;

  const {orders} = useOrdersStore();
  const {activeTab, isLoading, setActiveTab, setLoading} = appStore();
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  const [itemsPerPage] = useState(10); // Number of addresses displayed on each page
  const [currentPage, setCurrentPage] = useState(currentPageFromUrl);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Initialize filter state from URL parameters
  const [filter, setFilter] = useState({
    activeFilter: sortField === 'created_at' ? 'orderDate' : 'orderPrice',
    orderDate: sortField === 'created_at' ? sortDirection : ORDER.DESC,
    orderPrice: sortField === 'total_payment' ? sortDirection : ORDER.ASC,
  });

  const {orderDate, orderPrice, activeFilter} = filter || {};

  const pendingCount = useMemo(() => {
    const pendingStatus = (orders?.items || []).find(item => item?.status?.id === PRODUCT_STATUS.PENDING);
    return pendingStatus?.products?.length || 0;
  }, [orders?.items]);

  // Tính toán pagination
  const totalPages = Math.ceil(orders?.total / itemsPerPage);

  // Create a reusable function to update URL parameters
  const updateUrlWithFilters = useCallback((params) => {
    const newParams = new URLSearchParams(window.location.search);
    // Update each parameter provided
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, String(value));
      } else {
        newParams.delete(key);
      }
    });
    navigate(`?${newParams.toString()}`, {replace: true});
  }, [navigate]);

  const onChangeFilterOrderPrice = () => {
    let newDirection;
    if (orderPrice === ORDER.DESC) {
      newDirection = ORDER.ASC;
    } else {
      newDirection = ORDER.DESC;
    }

    // Only update state after the URL is updated to avoid double renders
    updateUrlWithFilters({
      sort_field: 'total_payment',
      sort_direction: newDirection
    });

    // Local state updates will happen via the URL change effect
  };

  const onChangeFilterOrderDate = () => {
    let newDirection;
    if (filter.activeFilter === 'orderDate') {
      if (filter.orderDate === ORDER.ASC) {
        newDirection = ORDER.DESC;
      } else {
        newDirection = ORDER.ASC;
      }
    } else {
      newDirection = filter.orderDate;
    }

    // Only update state after the URL is updated to avoid double renders
    updateUrlWithFilters({
      sort_field: 'created_at',
      sort_direction: newDirection
    });

    // Local state updates will happen via the URL change effect
  };

  const handleTabChange = useCallback((id) => {
    // Don't update if tab hasn't changed
    if (id === activeTab) return;

    setActiveTab(id);
    setCurrentPage(1);

    // Update URL with new status and reset page
    updateUrlWithFilters({
      status: id === 'all' ? '' : id,
      page: 1
    });
  }, [setActiveTab, updateUrlWithFilters, activeTab]);

  const handleSearch = useCallback((query) => {
    setLocalSearchQuery(query);
    setCurrentPage(1);
    updateUrlWithFilters({query, page: 1});
  }, [updateUrlWithFilters]);

  // Process when changing pages
  const handlePageChange = useCallback((page) => {
    if (page < 1 || page > totalPages) return;

    setCurrentPage(page);
    updateUrlWithFilters({page});
    window.scrollTo({top: 0, behavior: "smooth"});
  }, [totalPages, updateUrlWithFilters]);

  const handleShowRejected = () => {
    setActiveTab('pending');
    updateUrlWithFilters({status: 'pending'});
  };

  // Initial data loading - only runs once
  useEffect(() => {
    // Use Promise.all to make these requests in parallel
    Promise.all([
      fetchAddresses({page: 1, size: 10}),
      fetchOrderCountStatusAllTime()
    ]);

    // Empty dependency array means this runs once on mount
  }, []);

  // Effect for search query debouncing
  useEffect(() => {
    // Only set debounced query if it's different
    if (localSearchQuery === debouncedSearchQuery) return;

    const timer = setTimeout(() => {
      setDebouncedSearchQuery(localSearchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [localSearchQuery, debouncedSearchQuery]);

  // Effect for handling status from URL - only runs once on mount and when searchStatus changes
  useEffect(() => {
    if (searchStatus && searchStatus !== activeTab) {
      setActiveTab(searchStatus);
    }
  }, [searchStatus, setActiveTab, activeTab]);

  // Effect to sync URL parameters with component state
  useEffect(() => {
    // Only update state if values are different to prevent unnecessary renders
    if (currentPage !== currentPageFromUrl) {
      setCurrentPage(currentPageFromUrl);
    }

    if (localSearchQuery !== searchQuery) {
      setLocalSearchQuery(searchQuery);
    }

    const newActiveFilter = sortField === 'created_at' ? 'orderDate' : 'orderPrice';
    const newOrderDate = sortField === 'created_at' ? sortDirection : filter.orderDate;
    const newOrderPrice = sortField === 'total_payment' ? sortDirection : filter.orderPrice;

    // Only update filter if values have changed
    if (filter.activeFilter !== newActiveFilter ||
      filter.orderDate !== newOrderDate ||
      filter.orderPrice !== newOrderPrice) {
      setFilter({
        activeFilter: newActiveFilter,
        orderDate: newOrderDate,
        orderPrice: newOrderPrice,
      });
    }
  }, [searchParams, currentPageFromUrl, searchQuery, sortField, sortDirection]);

  // Main effect to fetch orders data - optimized to reduce redundant API calls
  useEffect(() => {
    // Track whether component is mounted to prevent state updates after unmount
    let isMounted = true;

    const fetchOrdersData = async () => {
      setLoading(true);
      const sort_field = filter.activeFilter === 'orderDate' ? 'created_at' : 'total_payment';
      const sort_direction = filter.activeFilter === 'orderDate' ? filter.orderDate : filter.orderPrice;

      try {
        const res = await fetchOrders({
          page: currentPage,
          size: itemsPerPage,
          search: debouncedSearchQuery,
          status: activeTab === 'all' ? '' : activeTab,
          sort_field: sort_field,
          sort_direction: sort_direction,
        });

        if (res.success && isMounted) {
          // Use a shorter timeout for better UX
          setTimeout(() => {
            if (isMounted) setLoading(false);
          }, 300);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
        if (isMounted) setLoading(false);
      }
    };

    // Use a debounce for the API call itself
    const timeoutId = setTimeout(() => {
      fetchOrdersData();
    }, 300); // Short delay to batch rapid changes

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [
    // Only include dependencies that should trigger a refetch
    debouncedSearchQuery,
    activeTab,
    currentPage,
    // For filter, only depend on the values actually used in the API call
    filter.activeFilter,
    filter.activeFilter === 'orderDate' ? filter.orderDate : filter.orderPrice
  ]);

  if (isMobile && isLoading) return <Loading/>;

  return (
    <div className="max-w-4xl mx-auto md:py-6 pb-24 ">
      {/* Header */}
      <div className='relative hidden md:flex gap-[5px] justify-center items-center mb-5'>
        <img
          className='w-6 h-6'
          src={IMG.icon.clipboardListActive}
          alt="orders icon"
        />
        <h1 className='text-sm md:text-xl font-bold'>
          {text.orderManagement}
        </h1>
      </div>
      {/* Search */}
      <div className="hidden md:flex items-center justify-between space-x-4 md:mt-4 px-5">
        <div className='w-[310px] md:w-[380px]'>
          <SearchBar
            value={localSearchQuery}
            onChange={handleSearch}
          />
        </div>
        <div
          className='flex w-full items-center justify-end gap-2.5 text-sm mt-4 md:mt-0 px-5 md:px-0'>
          <div className='flex justify-center items-center gap-2.5'>
            <p className='font-bold'>Sắp xếp theo: </p>
            <p
              className={cn('w-[65px] cursor-pointer', activeFilter === 'orderDate' && 'text-[#197D55]')}
              onClick={onChangeFilterOrderDate}>
              {orderDate === ORDER.DESC ? 'Mới nhất' : 'Cũ nhất'}</p>
          </div>
          <div
            className='flex justify-center items-center gap-2.5'>
            <div className={cn('font-bold')}>Giá:</div>
            <div
              className={clsx('font-normal flex items-center space-x-[10px] cursor-pointer hover:text-[#197D55] transition-transform-colors-opacity',
                orderPrice === ORDER.ASC && activeFilter === 'orderPrice' && 'text-[#197D55]')}
              onClick={onChangeFilterOrderPrice}>
              {!isMobile && <div>Tăng dần</div>}
              <IconArrowUp
                width={20}
                className={clsx(orderPrice === ORDER.ASC && activeFilter === 'orderPrice' ? 'text-[#197D55]' : 'text-black ',
                  'hover:text-[#197D55] transition-transform-colors-opacity')}/>
            </div>
            <div
              className={clsx('font-normal flex items-center space-x-[10px] cursor-pointer hover:text-[#197D55] transition-transform-colors-opacity',
                orderPrice === ORDER.DESC && activeFilter === 'orderPrice' && 'text-[#197D55]')}
              onClick={onChangeFilterOrderPrice}>
              {!isMobile && <div>Giảm dần</div>}
              <IconArrowDown
                width={20}
                className={clsx(orderPrice === ORDER.DESC && activeFilter === 'orderPrice' ? 'text-[#197D55]' : 'text-black ',
                  'hover:text-[#197D55] transition-transform-colors-opacity')}/>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className='md:pt-4 md:mt-4 md:border-t border-b border-gray-200 bg-white'>
        <OrderTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>
      {(!activeTab?.id && pendingCount > 0 && activeTab !== 'pending') &&
        <div className='md:hidden flex justify-between rounded-md bg-[#FFF9E3] text-xs p-[5px] mb-[5px] px-4'>
          <IconText
            icon={IMG.icon.exclamationRed}
            iconClass='w-3 h-3'
            textChild={<p>Có <span className='text-red-600'>{pendingCount || 0}</span> đơn đang chờ bạn xác
              nhận
            </p>}
          />
          <Button className='bg-[#F8C51880] text-[10px] h-3' onClick={handleShowRejected}>
            {text.viewNow}
          </Button>
        </div>
      }
      <div className='md:hidden w-full px-4 py-2 bg-white my-4'>
        <SearchBar
          value={localSearchQuery}
          onChange={handleSearch}
        />
      </div>

      {/* Orders List */}
      <div className="space-y-4 bg-[#EDEDED] md:bg-white md:pb-4 md:p-4">
        {isLoading && !isMobile ? <Loading isCustom/> :
          !isEmpty(orders?.items) ? (
            orders?.items.map(order => (
              <OrderGroup
                key={order.id}
                group={order}
              />
            ))
          ) : (
            <Empty title={text.emptyOrder} minHeight={600}/>
          )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pb-6 pt-3 bg-white md:rounded-b-2xl">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}
